import React, { createContext, useContext, useState, useRef, useCallback } from 'react';

const LoadingContext = createContext();

// Timeout duration in milliseconds (20 seconds)
const BACKEND_TIMEOUT_MS = 20000;

export const LoadingProvider = ({ children }) => {
  const [isConfiguratorLoading, setIsConfiguratorLoading] = useState(true);
  const [isBackendLoading, setIsBackendLoading] = useState(false);
  // Track which specific field triggered the backend call
  const [loadingField, setLoadingField] = useState(null);
  // Track WebSocket connection status
  const [isWebSocketConnected, setIsWebSocketConnected] = useState(false);
  // Track timeout status
  const [hasTimedOut, setHasTimedOut] = useState(false);

  // Ref for timeout timer
  const timeoutRef = useRef(null);

  // Start loading for a specific field (with timeout)
  const startFieldLoading = useCallback((fieldName) => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    setIsBackendLoading(true);
    setLoadingField(fieldName);
    setHasTimedOut(false);

    // Start timeout timer
    timeoutRef.current = setTimeout(() => {
      setHasTimedOut(true);
      // Unblock the form so user can retry
      setIsBackendLoading(false);
      setLoadingField(null);
    }, BACKEND_TIMEOUT_MS);
  }, []);

  // Stop loading (and clear timeout)
  const stopFieldLoading = useCallback(() => {
    // Clear timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    setIsBackendLoading(false);
    setLoadingField(null);
    setHasTimedOut(false);
  }, []);

  return (
    <LoadingContext.Provider value={{
      isConfiguratorLoading,
      setIsConfiguratorLoading,
      isBackendLoading,
      setIsBackendLoading,
      loadingField,
      startFieldLoading,
      stopFieldLoading,
      isWebSocketConnected,
      setIsWebSocketConnected,
      hasTimedOut
    }}>
      {children}
    </LoadingContext.Provider>
  );
};

export const useLoading = () => {
  const context = useContext(LoadingContext);
  if (!context) {
    throw new Error('useLoading must be used within LoadingProvider');
  }
  return context;
};
